<template>
  <div class="header-actions">
    <!-- Bouton de rafraîchissement complet (F5) -->
    <button
      class="action-button refresh-button"
      :class="{'disabled': !isOnline || isUserOfflineMode}"
      :disabled="!isOnline || isUserOfflineMode"
      @click="$emit('refresh')"
      v-tooltip.top="'Actualiser la page'"
      aria-label="Actualiser la page"
    >
      <span class="button-content">
        <i class="pi pi-refresh"></i>
        <span class="button-label">Actualiser l'application</span>
      </span>
    </button>
    
    <!-- Bouton de synchronisation -->
    <button
      class="action-button sync-button"
      :class="{
        'disabled': isSyncing || (!isOnline && !isUserOfflineMode),
        'pending': hasPendingUpdates,
        'error': hasManualSyncNeeded
      }"
      :disabled="isSyncing || (!isOnline && !isUserOfflineMode)"
      @click="$emit('sync')"
      v-tooltip.top="syncTooltip"
      aria-label="Synchroniser les données"
    >
      <span class="button-content">
        <i class="pi pi-sync" v-if="!isSyncing"></i>
        <ProgressSpinner v-else class="spinner" strokeWidth="4" />
        <span class="button-label">Synchroniser les données</span>
        <span v-if="hasPendingUpdates" class="status-badge" :class="{ 'error-badge': hasManualSyncNeeded }">
          {{ pendingUpdatesCount || '!' }}
        </span>
      </span>
    </button>
  </div>
</template>

<script setup>
import ProgressSpinner from 'primevue/progressspinner';

// Props
const props = defineProps({
  isOnline: {
    type: Boolean,
    default: true
  },
  isUserOfflineMode: {
    type: Boolean,
    default: false
  },
  isSyncing: {
    type: Boolean,
    default: false
  },
  hasPendingUpdates: {
    type: Boolean,
    default: false
  },
  hasManualSyncNeeded: {
    type: Boolean,
    default: false
  },
  pendingUpdatesCount: {
    type: Number,
    default: 0
  },
  syncTooltip: {
    type: String,
    default: 'Synchroniser les données'
  }
});

// Emit events
const emit = defineEmits(['refresh', 'sync']);
</script>

<style scoped>
.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: -2rem;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #ffffff, #f5f7fa);
  border: 1px solid rgba(229, 231, 235, 0.5);
  border-radius: 0.65rem;
  padding: 0.25rem 0.65rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: #4B5563;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  height: 3rem;
  position: relative;
  overflow: hidden;
}

.action-button:hover:not(.disabled) {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

.action-button:active:not(.disabled) {
  transform: translateY(0);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

.action-button.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

.button-content {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  position: relative;
}

.button-label {
  display: none; /* Sur mobile */
}

@media (min-width: 641px) {
  .button-label {
    display: inline-flex;
    font-size: 0.95rem;
  }
}

@media (max-width: 640px) {
  .header-actions {
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0;
  }

  .action-button {
    flex: 1;
    width: 50%;
    max-width: 50%;
    padding: 0.2rem 0.4rem;
    height: 3rem;
    border-radius: 0.5rem;
  }

  .button-label {
    display: inline-flex;
    font-size: 0.85rem;
  }
  
  .sync-button {
    margin-left: 0 !important;
  }
}

/* Styles spécifiques au bouton de rafraîchissement */
.refresh-button:not(.disabled) {
  background: linear-gradient(145deg, #f9fafb, #f3f4f6);
}

.refresh-button:not(.disabled):hover {
  background: linear-gradient(145deg, #ffffff, #f9fafb);
}

.refresh-button:not(.disabled):hover i {
  animation: rotate360 0.5s ease;
}

/* Styles spécifiques au bouton de synchronisation */
.sync-button.pending {
  border-color: rgba(251, 191, 36, 0.6);
  background: linear-gradient(145deg, #fef3c7, #fde68a);
  color: #92400E;
}

.sync-button.error {
  border-color: rgba(248, 113, 113, 0.6);
  background: linear-gradient(145deg, #fee2e2, #fca5a5);
  color: #B91C1C;
}

.sync-button:not(.disabled):hover i {
  animation: rotateSync 0.5s ease;
}

.spinner {
  width: 1.25rem !important;
  height: 1.25rem !important;
}

/* Badge de notification */
.status-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  border-radius: 9px;
  background-color: #FBBF24;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  padding: 0 5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  animation: pulse 2s infinite;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-badge.error-badge {
  background-color: #EF4444;
}

/* Animations */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes rotate360 {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes rotateSync {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(180deg);
  }
}
</style>